import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import "../../styles/IntakeSheet.css";

import AssignmentsOfBenifit from "./assignmentOfBenefit/AssignmentOfBenefit";
import HIPAADeclaration     from "./hippaDeclaration/HippaDeclaration";
import MedicalLien          from "./medicalLien/MedicalLien";
import NF2                  from "./nf2/NF2";
import EPAT from "./epat/EpatConsent";
import HippaAuth from "./hippaAuth/HippaAuthorization";
import { GetPatientDetails } from "../patientDetail/Service";
import toast from "react-hot-toast";
import IntakeForm from "./intakeForm/IntakeForm";
import PregnancyConsent from "./pregnancyConsent/PregnancyConsent";
import ChiropracticCareConsent from "./chiropracticCareConsent/ChiropracticCareConsent";
import TriggerPointConSent from "./triggerpointinjectionconsent/TriggerPointInjectionConsent";
import AcupunctureConsent from "./acupunctureConsent/AcupunctureConsent";
import PatientConsentForm from "./patientConsent/PatientConsent";
import MinorConsentForm from "./minorConsent/MinorConsent";
import DynamicIntakeTab from "../formBuilder/DynamicIntakeTab"; // ── NEW: Form Builder tabs


// ── Shared-signature session key ─────────────────────────────────────────────
const SHARED_SIG_KEY = "sharedPatientSignature";

const handleGetPatientDetails = async () => {
  try {
    const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");
    const caseId = tokenData?.caseId || tokenData?.CaseId;
    const companyId = tokenData?.companyId || tokenData?.CompanyId;
    if (!caseId || !companyId) {
      toast.error("Something went wrong while fetching details.");
      return;
    }
    const response = await GetPatientDetails(caseId, companyId);
    if (response.success) {
      const details = response.data;
      if (details) {
        return details;
      } else {
        toast.error("Something went wrong while fetching details.");
      }
    } else {
      toast.error("Something went wrong while fetching details.");
      return {};
    }
  } catch (error) {
    toast.error("Something went wrong while fetching details.");
    return {};
  }
};

const ALL_TABS = [
  { id: "aob",             intakeSheetId: 1,  label: "Assignment of Benefits"                },
  { id: "hipaa_auth",      intakeSheetId: 2,  label: "HIPAA Authorization"                   },
  { id: "hipaa_decl",      intakeSheetId: 3,  label: "HIPAA Declaration"                     },
  { id: "med_lien",        intakeSheetId: 6,  label: "Medical LIEN"                          },
  { id: "nf2",             intakeSheetId: 4,  label: "NF 2"                                  },
  { id: "epat",            intakeSheetId: 7,  label: "EPAT Consent"                          },
  { id: "preg_consent",    intakeSheetId: 23,  label: "Pregnancy Consent"                    },
  { id: "chiro_consent",   intakeSheetId: 33, label: "Informed Consent To Chiropractic Care"       },
  { id: "trigger_point_consent",   intakeSheetId: 8, label: "Trigger Point Injection Consent"       },
  { id: "pain_contract",   intakeSheetId: 11, label: "Doctor Patient Pain Contract"          },
  { id: "patient_consent", intakeSheetId: 15, label: "Patient Consent"                       },
  { id: "power_atty",      intakeSheetId: 13, label: "J Durable Power of Attorney"           },
  { id: "intake_form",    intakeSheetId:   5,  label: "Intake Form"                            },
  { id: "acupuncture_consent",    intakeSheetId: 25,  label: "Acupuncture Informed Consent To Treat"  },
  { id: "minor_consent", intakeSheetId: 12, label: "Minor Consent"                       },
];

function resolveAllowedTabs() {
  try {
    const raw = sessionStorage.getItem("tokenIntakeSheets");
    if (!raw) return [];
    const tokenSheets = JSON.parse(raw);
    const result = [];
    const usedIds = new Set();

    for (const s of tokenSheets) {
      const rawId   = s.IntakeSheetId ?? s.intakeSheetId;
      const rawName = s.IntakeSheetName ?? s.intakeSheetName ?? "";
      const numId   = Number(rawId);

      if (numId < 0) {
        // Negative ID = Form Builder template (formTemplateId = Math.abs(numId))
        const formTemplateId = Math.abs(numId);
        const tabId = `fb_${formTemplateId}`;
        if (!usedIds.has(tabId)) {
          usedIds.add(tabId);
          result.push({ id: tabId, intakeSheetId: tabId, label: rawName, isFormBuilder: true });
        }
      } else {
        // Positive ID = legacy tab — match by ID in ALL_TABS
        const tab = ALL_TABS.find(t => t.intakeSheetId === numId);
        if (tab && !usedIds.has(tab.id)) {
          usedIds.add(tab.id);
          result.push(tab);
        }
      }
    }
    return result;
  } catch (e) {
    console.error('[resolveAllowedTabs] error:', e);
    return [];
  }
}

function ISec({ title, first }) {
  return (
    <div className={`intake-sheet__section${first ? " intake-sheet__section--first" : ""}`}>
      <span className="intake-sheet__section-title">{title}</span>
      <span className="intake-sheet__section-line" />
    </div>
  );
}

function IField({ label, children, span }) {
  const spanClass =
    span === 2 ? " intake-sheet__field--span-2" :
    span === 3 ? " intake-sheet__field--span-3" : "";
  return (
    <div className={`intake-sheet__field${spanClass}`}>
      <label className="intake-sheet__label">{label}</label>
      {children}
    </div>
  );
}

function IntakePlaceholder({ title }) {
  return (
    <div className="intake-sheet__inner">
      <ISec title={title} first />
      <div className="intake-sheet__placeholder">
        <div className="intake-sheet__placeholder-icon">📄</div>
        <div className="intake-sheet__placeholder-title">{title}</div>
        <div className="intake-sheet__placeholder-desc">
          Form fields for this section will appear here.
        </div>
      </div>
      <div className="intake-sheet__grid-2">
        <IField label="Patient Name">
          <input className="intake-sheet__input" placeholder="Full name" />
        </IField>
        <IField label="Date">
          <input className="intake-sheet__input" type="date"
            defaultValue={new Date().toISOString().split("T")[0]} />
        </IField>
      </div>
      <div className="intake-sheet__grid-2" style={{ marginBottom: 6 }}>
        <IField label="Patient Signature">
          <input className="intake-sheet__input" placeholder="Type full name to sign" />
        </IField>
        <IField label="Witness Signature">
          <input className="intake-sheet__input" placeholder="Witness full name" />
        </IField>
      </div>
    </div>
  );
}

// ── Tab wrappers – each now forwards applySignature ──────────────────────────

const TabAOB = forwardRef(function TabAOB(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
    getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <AssignmentsOfBenifit ref={inner} {...props} />;
});

const TabHIPAAAuth = forwardRef(function TabHIPAAAuth(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <HippaAuth ref={inner} {...props} />;
});

const TabHIPAADecl = forwardRef(function TabHIPAADecl(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <HIPAADeclaration ref={inner} {...props} />;
});

const TabEPAT = forwardRef(function TabEPAT(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <EPAT ref={inner} {...props} />;
});
const TabPregConsent = forwardRef(function TabPregConsent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <PregnancyConsent ref={inner} {...props} />;
});


const TabChiroConsent = forwardRef(function TabChiroConsent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <ChiropracticCareConsent ref={inner} {...props} />;
});

const TabTriggerPointConSent = forwardRef(function TabTriggerPointConSent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <TriggerPointConSent ref={inner} {...props} />;
});
const TabAcupunctureConSent = forwardRef(function TabAcupunctureConSent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <AcupunctureConsent ref={inner} {...props} />;
});

const TabMedicalLien = forwardRef(function TabMedicalLien(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <MedicalLien ref={inner} {...props} />;
});

const TabNF2 = forwardRef(function TabNF2(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <NF2 ref={inner} {...props} />;
});

const TabIntakeForm = forwardRef(function TabIntakeForm(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             () => inner.current?.save(),
    print:            () => inner.current?.print(),
    isPending:        () => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig) => inner.current?.applySignature?.(sig),
  }));
  return <IntakeForm ref={inner} {...props} />;
});

const TabPatientConsent = forwardRef(function TabPatientConsent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <PatientConsentForm ref={inner} {...props} />;
});

const TabMinorConsent = forwardRef(function TabMinorConsent(props, ref) {
  const inner = useRef(null);
  useImperativeHandle(ref, () => ({
    save:             ()        => inner.current?.save(),
    print:            (savePdf) => inner.current?.print(savePdf),
    isPending:        ()        => inner.current?.isPending?.(),
     getSignature:   () => inner.current?.getSignature?.() || "",  // ← ADD
    applySignature:   (sig)     => inner.current?.applySignature?.(sig),
  }));
  return <MinorConsentForm ref={inner} {...props} />;
});

function TabComponent({ tabId, tabRef, sharedProps }) {
  switch (tabId) {
    case "aob":             return <TabAOB        ref={tabRef} {...sharedProps} />;
    case "hipaa_auth":      return <TabHIPAAAuth   ref={tabRef} {...sharedProps} />;
    case "hipaa_decl":      return <TabHIPAADecl   ref={tabRef} {...sharedProps} />;
    case "med_lien":        return <TabMedicalLien ref={tabRef} {...sharedProps} />;
    case "nf2":             return <TabNF2         ref={tabRef} {...sharedProps} />;
    case "epat":            return <TabEPAT        ref={tabRef} {...sharedProps} />;
    case "intake_form":     return <TabIntakeForm  ref={tabRef} {...sharedProps} />;
    case "preg_consent":    return <TabPregConsent        ref={tabRef} {...sharedProps} />;
    case "chiro_consent":   return <TabChiroConsent        ref={tabRef} {...sharedProps} />;
    case "trigger_point_consent":   return <TabTriggerPointConSent        ref={tabRef} {...sharedProps} />;
    case "acupuncture_consent":   return <TabAcupunctureConSent        ref={tabRef} {...sharedProps} />;
    case "patient_consent": return <TabPatientConsent  ref={tabRef} {...sharedProps} />;
    case "minor_consent": return <TabMinorConsent  ref={tabRef} {...sharedProps} />;
    case "trigger":         return <IntakePlaceholder title="Trigger Point Injection Consent" />;
    case "pain_contract":   return <IntakePlaceholder title="Doctor Patient Pain Contract" />;
    case "power_atty":      return <IntakePlaceholder title="J Durable Power of Attorney" />;
    default:
      // ── NEW: Form Builder dynamic tab ────────────────────────────────────
      if (String(tabId).startsWith("fb_")) {
        return <DynamicIntakeTab ref={tabRef} tabId={tabId} onReady={sharedProps.onReady} copySignature={sharedProps.copySignature} />;
      }
      return null;
  }
}

const PendingBadge = () => (
  <span style={{ fontSize: 10, fontWeight: 600, color: "#f97316" }}>
    Pending
  </span>
);

function ScrollableTabs({ tabs, activeId, onSelect, tabStatus = {} }) {
  const scrollRef = useRef(null);
  const [canLeft,  setCanLeft]  = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 2);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  const setRef = el => {
    scrollRef.current = el;
    if (el) {
      el.addEventListener("scroll", checkScroll, { passive: true });
      setTimeout(checkScroll, 0);
    }
  };

  const scroll = dir => scrollRef.current?.scrollBy({ left: dir * 180, behavior: "smooth" });

  const ArrowBtn = ({ dir }) => {
    const visible = dir === -1 ? canLeft : canRight;
    return (
      <button
        onClick={() => scroll(dir)}
        className={`intake-sheet__arrow intake-sheet__arrow--${dir === -1 ? "left" : "right"}${!visible ? " intake-sheet__arrow--hidden" : ""}`}
        aria-hidden={!visible}
        tabIndex={visible ? 0 : -1}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="var(--is-t600)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {dir === -1
            ? <polyline points="15 18 9 12 15 6" />
            : <polyline points="9 18 15 12 9 6" />}
        </svg>
      </button>
    );
  };

  return (
    <div className="intake-sheet__tabs-wrapper">
      <div className="intake-sheet__tabs-row">
        <ArrowBtn dir={-1} />
        <div ref={setRef} className="intake-sheet__tabs-scroll" onScroll={checkScroll}>
          {tabs.map(tab => {
            const isActive  = activeId === tab.id;
            const isPending = tabStatus[tab.id] === true;
            return (
              <button
                key={tab.id}
                onClick={() => onSelect(tab.id)}
                className={`intake-sheet__tab${isActive ? " intake-sheet__tab--active" : ""}`}
                aria-selected={isActive}
                role="tab"
              >
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                  <span>{tab.label}</span>
                  {isPending && <PendingBadge />}
                </span>
              </button>
            );
          })}
        </div>
        <ArrowBtn dir={1} />
      </div>
    </div>
  );
}

function NoSheetsState() {
  return (
    <div style={{
      padding: "48px 24px", textAlign: "center", color: "#7d6e61",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
    }}>
      <span style={{ fontSize: 36 }}>📋</span>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#3d332a" }}>No intake sheets assigned</div>
      <div style={{ fontSize: 13, maxWidth: 340, lineHeight: 1.6 }}>
        No intake sheets were included in your session token.
        Please contact your administrator to update your access link.
      </div>
    </div>
  );
}

const IntakeSheet = forwardRef(function IntakeSheet({
  initialTab,
  onTabChange,
  onTabInfoChange,
  selectedRow,
  BaseUrl,
  intakeSheet,
  providerOptions = [],
  sessionData,
  signatureMiss,
  copySignature,       // ← boolean from PatientPortal checkbox
  onApiSuccess,
  onPendingChange,
}, ref) {

  const [allowedTabs] = useState(resolveAllowedTabs);

  const [activeTab, setActiveTab] = useState(() => {
    if (!allowedTabs.length) return null;
    if (initialTab && allowedTabs.some(t => t.id === initialTab)) return initialTab;
    return allowedTabs[0].id;
  });

  useEffect(() => {
    if (!allowedTabs.length || !activeTab) {
      onTabInfoChange?.({ isLastTab: true, isOnlyTab: true, totalTabs: 0, currentIndex: 0 });
      return;
    }
    const currentIndex = allowedTabs.findIndex(t => t.id === activeTab);
    const isLastTab = currentIndex === allowedTabs.length - 1;
    const isOnlyTab = allowedTabs.length === 1;
    onTabInfoChange?.({
      isLastTab,
      isOnlyTab,
      totalTabs: allowedTabs.length,
      currentIndex,
      activeTab,
    });
  }, [activeTab, allowedTabs, onTabInfoChange]);

  const tabRefsMap = useRef({});
  const getTabRef = (tabId) => {
    if (!tabRefsMap.current[tabId]) {
      tabRefsMap.current[tabId] = { current: null };
    }
    return tabRefsMap.current[tabId];
  };

  const [tabStatus, setTabStatus] = useState(() => {
    const initialStatus = {};
    allowedTabs.forEach(tab => {
      initialStatus[tab.id] = true;
    });
    return initialStatus;
  });

  const handleTabReady = useCallback((tabId) => {
  const tabRef = tabRefsMap.current[tabId];
  if (!tabRef?.current?.isPending) return;
  const pending = tabRef.current.isPending();
  setTabStatus(prev => ({ ...prev, [tabId]: pending }));

  // ── Auto-apply when tab becomes ready and copySignature is ON ──
  if (copySignature) {
    // Get sig from the ACTIVE tab, not sessionStorage
    const activeTabRef = tabRefsMap.current[activeTab];
    const sig = activeTabRef?.current?.getSignature?.()
      || sessionStorage.getItem(SHARED_SIG_KEY);

    if (sig && tabId !== activeTab) {
      setTimeout(() => {
        tabRefsMap.current[tabId]?.current?.applySignature?.(sig);
      }, 300);
    }
  }
}, [copySignature, activeTab]);

  const refreshActiveTabStatus = useCallback(() => {
    if (!activeTab) return;
    const tabRef = tabRefsMap.current[activeTab];
    if (!tabRef?.current?.isPending) return;
    const pending = tabRef.current.isPending();
    setTabStatus(prev => ({ ...prev, [activeTab]: pending }));
  }, [activeTab]);

  useEffect(() => {
  sessionStorage.removeItem(SHARED_SIG_KEY);
}, []); // clear on mount so stale sig never causes false positive

  // ── Broadcast to all mounted tabs when copySignature toggles ON ──────────
  // useEffect(() => {
  //   if (!copySignature) return;
  //   const sig = sessionStorage.getItem(SHARED_SIG_KEY);
  //   if (!sig) return;
  //   allowedTabs.forEach(tab => {
  //     tabRefsMap.current[tab.id]?.current?.applySignature?.(sig);
  //   });
  // }, [copySignature]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Broadcast to all mounted tabs when copySignature toggles ON ──────────


useEffect(() => {
  if (!copySignature) return;

  const timer = setTimeout(() => {
    const activeTabRef = tabRefsMap.current[activeTab];
    // Only trust live ref, NOT sessionStorage
    const sig = activeTabRef?.current?.getSignature?.() || "";

    if (!sig) {
      toast.error("Signature is required.");
      return;
    }

    sessionStorage.setItem(SHARED_SIG_KEY, sig);

    allowedTabs.forEach(tab => {
      if (tab.id === activeTab) return;
      tabRefsMap.current[tab.id]?.current?.applySignature?.(sig);
    });
  }, 100);

  return () => clearTimeout(timer);
}, [copySignature]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const anyPending = Object.values(tabStatus).some(Boolean);
    onPendingChange?.(anyPending);
  }, [tabStatus, onPendingChange]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("tokenIntakeSheets");
      if (!raw) return;
      const sheets = JSON.parse(raw);
      let changed = false;
      sheets.forEach(s => {
        const rawId = s.IntakeSheetId ?? s.intakeSheetId;
        const numId = Number(rawId);
        let tabId;
        if (numId < 0) {
          tabId = `fb_${Math.abs(numId)}`;
        } else {
          const tab = ALL_TABS.find(t => t.intakeSheetId === numId);
          if (tab) tabId = tab.id;
        }
        if (tabId && tabStatus[tabId] !== undefined) {
          const isSaved = !tabStatus[tabId];
          if (s.IsSaved !== isSaved && s.isSaved !== isSaved) {
            if (s.hasOwnProperty("IsSaved")) s.IsSaved = isSaved;
            if (s.hasOwnProperty("isSaved")) s.isSaved = isSaved;
            if (!s.hasOwnProperty("IsSaved") && !s.hasOwnProperty("isSaved")) {
              s.IsSaved = isSaved;
            }
            changed = true;
          }
        }
      });
      if (changed) {
        sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(sheets));
      }
    } catch (e) {
      console.error("Failed to sync tabStatus to sessionStorage", e);
    }
  }, [tabStatus]);

  useImperativeHandle(ref, () => ({
   submit: async ({ skipTabAdvance = false } = {}) => {
      const activeRef = tabRefsMap.current[activeTab];
      const saveResult = await activeRef?.current?.save?.();
      if (saveResult === false) return;

      refreshActiveTabStatus();

      if (skipTabAdvance) {
      onApiSuccess?.();
      return;
    }

      const currentIndex = allowedTabs.findIndex(t => t.id === activeTab);
      const isLastTab = currentIndex === allowedTabs.length - 1;

      if (isLastTab) {
        onApiSuccess?.();
      } else {
        const nextTab = allowedTabs[currentIndex + 1];
        setActiveTab(nextTab.id);
        onTabChange?.(nextTab.id);
      }
    },
    hasPending: () => Object.values(tabStatus).some(Boolean),

    hasSignature: () => {
      const activeRef = tabRefsMap.current[activeTab];
      const sig = activeRef?.current?.getSignature?.() || "";
      return !!sig;  // ← removed sessionStorage fallback
    },
    isLastTab: () => {
      const currentIndex = allowedTabs.findIndex(t => t.id === activeTab);
      return currentIndex === allowedTabs.length - 1;
    },
    isOnlyTab: () => allowedTabs.length === 1,
  }), [allowedTabs, activeTab, onApiSuccess, onTabChange, refreshActiveTabStatus, tabStatus]);

  const handleSelect = async (id) => {
    if (activeTab === id) return;
    
    const activeRef = tabRefsMap.current[activeTab];
    if (activeRef?.current?.save) {
      const saveResult = await activeRef.current.save();
      // If the save fails (e.g. validation error), do not switch tab
      if (saveResult === false) return;
      refreshActiveTabStatus();
    }

    setActiveTab(id);
    onTabChange?.(id);
  };

  const tokenData = JSON.parse(sessionStorage.getItem("tokenData") || "{}");

  const sharedProps = {
    selectedRow,
    BaseUrl,
    intakeSheet,
    providerOptions,
    sessionData,
    signatureMiss,
    copySignature,
    onApiSuccess,
    onReady: handleTabReady,
    companyId: tokenData?.companyId || tokenData?.CompanyId,
  };

  if (!allowedTabs.length || !activeTab) {
    return (
      <div className="intake-sheet" role="tabpanel">
        <NoSheetsState />
      </div>
    );
  }

  return (
    <div className="intake-sheet" role="tabpanel">
      <ScrollableTabs
        tabs={allowedTabs}
        activeId={activeTab}
        onSelect={handleSelect}
        tabStatus={tabStatus}
      />
      <div className="intake-sheet__content">
        <div className="intake-sheet__content-accent" />
        {allowedTabs.map(tab => (
          <div
            key={tab.id}
            style={{ display: tab.id === activeTab ? "block" : "none" }}
          >
            <TabComponent
              tabId={tab.id}
              tabRef={getTabRef(tab.id)}
              sharedProps={sharedProps}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default IntakeSheet;